import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Skeleton } from "primereact/skeleton";
import type { Toast } from "primereact/toast";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import type { Socket } from "socket.io-client";
import AppShell from "../components/AppShell";
import AppToast from "../components/AppToast";
import DifficultyMeter from "../components/DifficultyMeter";
import EmptyState from "../components/EmptyState";
import GameCodeChip from "../components/GameCodeChip";
import { usePageTitle } from "../components/PageTitle";
import StatusPill from "../components/StatusPill";
import { useGlobalCountdown } from "../hooks/useGlobalCountdown";
import { useLiveRefresh } from "../hooks/useLiveRefresh";
import { acquireSocket, releaseSocket } from "../lib/socket";
import { normalizeDifficulty } from "../lib/difficulty";
import { isWelcomeChallenge } from "../lib/welcome";
import { applyDirection, useDirection } from "../lib/direction";
import type {
  AttachmentDto,
  ChallengeContent,
  ChallengeDto,
  ScoreboardDto,
} from "../types/api";

type Player = { id: string; name: string };
type Team = { id: string; name: string };
type Attachment = {
  title: string;
  filename: string;
  sort_index?: number;
};
type Challenge = {
  id: string | number;
  name: string;
  category?: string;
  difficulty?: string;
  duration?: string;
  scenarios?: number;
  description?: string;
  points?: number;
  flagFormat?: string;
  hint?: string;
  raw?: ChallengeDto;
  content?: ChallengeContent;
  attachments?: Attachment[];
  bundleUrl?: string | null;
  bundleSize?: number | null;
};

/** The gateway challenge everything else is gated behind. */
function isWelcome(c: Challenge | null | undefined): boolean {
  return isWelcomeChallenge(c?.raw);
}

/** Feeds the cursor position to the card's spotlight border. */
function onCardMove(e: React.MouseEvent<HTMLElement>) {
  const el = e.currentTarget;
  const r = el.getBoundingClientRect();
  el.style.setProperty("--mx", `${e.clientX - r.left}px`);
  el.style.setProperty("--my", `${e.clientY - r.top}px`);
}

export default function Play() {
  const { t, i18n } = useTranslation();
  const { isRtl } = useDirection();

  useEffect(() => {
    applyDirection(i18n.language);
  }, [i18n.language]);

  usePageTitle(t("app.title"));
  const [solvedMap, setSolvedMap] = useState<Record<string, boolean>>({});
  const params = useParams<{ gameCode: string }>();
  const [sp] = useSearchParams();
  const gameCode = useMemo(
    () => params.gameCode || sp.get("code") || "",
    [params.gameCode, sp]
  );

  const me = safeParse<Player>(localStorage.getItem("player"));
  const team = safeParse<Team>(localStorage.getItem("team"));
  /*
   * Redirecting here would skip every hook below, and React crashes with
   * "rendered fewer hooks than expected" if the session disappears while the
   * page is open. Instead the hooks always run, each one no-ops while the
   * session is incomplete, and the redirect happens at render time below.
   */
  const hasSession = Boolean(gameCode && me && team);
  const player: Player = me ?? { id: "", name: "" };
  const userTeam: Team = team ?? { id: "", name: "" };

  const [teamPoints, setTeamPoints] = useState(0);
  const toastRef = useRef<Toast>(null);
  const toastRefRight = useRef<Toast>(null);
  const [teamName, setTeamName] = useState(userTeam.name);

  // ---------- shared socket (one connection per tab) ----------
  const socketRef = useRef<Socket | null>(null);
  useEffect(() => {
    if (!hasSession) return;
    const s = acquireSocket();
    socketRef.current = s;

    const join = () => {
      s.emit("game:join", gameCode);
      s.emit("team:join", userTeam.id);
    };

    const onRenamed = (msg: { team_id: string; name: string }) => {
      if (msg?.team_id !== userTeam.id) return;
      setTeamName(msg.name);
      const curr = safeParse<Team>(localStorage.getItem("team")) || {
        id: userTeam.id,
        name: msg.name,
      };
      localStorage.setItem("team", JSON.stringify({ ...curr, name: msg.name }));
    };

    const onError = (err: Error) => {
      console.warn("socket connect_error:", err?.message || err);
    };

    s.on("connect", join);
    s.on("team:renamed", onRenamed);
    s.on("connect_error", onError);
    if (s.connected) join();

    return () => {
      /* Detach only our own handlers: the socket is shared. */
      s.off("connect", join);
      s.off("team:renamed", onRenamed);
      s.off("connect_error", onError);
      socketRef.current = null;
      releaseSocket();
    };
  }, [gameCode, userTeam.id, hasSession]);

  /* Team points update when the server says the scoreboard changed. */
  const { revision: scoreRevision } = useLiveRefresh({ gameCode });

  // ---------- countdown (shares the same socket internally) ----------
  const { status, label } = useGlobalCountdown(gameCode);

  // filters & list
  const [query, setQuery] = useState("");
  const [diff, setDiff] = useState<string | null>(null);
  const [cat, setCat] = useState<string | null>(null);
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  /* Bumped by the retry button to re-run the challenge fetch. */
  const [reloadKey, setReloadKey] = useState(0);

  const categories = useMemo(
    () =>
      Array.from(
        new Set(
          challenges.map((c) =>
            (c.category ?? "Uncategorized").toString().trim()
          )
        )
      ).sort(),
    [challenges]
  );

  // modal & actions
  const [show, setShow] = useState(false);
  const [selected, setSelected] = useState<Challenge | null>(null);
  const [flag, setFlag] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // hints
  const [usedHints, setUsedHints] = useState<Set<number>>(new Set());
  const [openedHints, setOpenedHints] = useState<Set<number>>(new Set());
  const toggleHint = (i: number) =>
    setOpenedHints((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  const attachmentLabel = (idx: number, total: number) =>
    total > 1
      ? `${t("submitmodal.attachment")} ${idx + 1}`
      : t("submitmodal.attachment");
  const API_BASE = import.meta.env.VITE_API_BASE ?? "/api";

  /* Memoised so effects can depend on it without being re-run every render. */
  const apiUrl = useCallback(
    (path: string, params?: Record<string, string | number>) => {
      const base = /^https?:\/\//i.test(API_BASE)
        ? new URL(API_BASE)
        : new URL(API_BASE, window.location.origin);
      const basePath = base.pathname.endsWith("/")
        ? base.pathname.slice(0, -1)
        : base.pathname;
      const suffix = path.startsWith("/") ? path : `/${path}`;
      const u = new URL(`${base.origin}${basePath}${suffix}`);
      if (params) {
        for (const [k, v] of Object.entries(params)) {
          u.searchParams.set(k, String(v));
        }
      }
      return u.toString();
    },
    [API_BASE]
  );

  function requestHint(i: number, hintsArr: string[]) {
    if (!hintsArr[i]) return;

    const isOpen = openedHints.has(i);
    if (isOpen || usedHints.has(i)) {
      toggleHint(i);
      return;
    }

    confirmDialog({
      header: `${t("submitmodal.revealHint")} ${i + 1} ${t("common.question")}`,
      message: <span>{t("submitmodal.revealHintText")}</span>,
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      rejectClassName: "p-button-text",
      acceptLabel: t("common.yes"),
      rejectLabel: t("common.no"),
      accept: async () => {
        try {
          const res = await fetch(
            apiUrl(
              `/public/games/${encodeURIComponent(
                gameCode
              )}/challenges/${encodeURIComponent(
                selected?.id || ""
              )}/hints/${i}/use`
            ),
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ teamName }),
            }
          );

          const data = await res.json().catch(() => ({}));
          if (res.ok && data?.ok) {
            setUsedHints((prev) => new Set([...prev, i]));
            toastRef.current?.show({
              severity: "info",
              summary: t("submissiontoast.hintRevealed"),
              detail: t("submissiontoast.hintRevealedText"),
              life: 2500,
            });
            toggleHint(i);
          } else {
            toastRef.current?.show({
              severity: "info",
              summary: t("submissiontoast.alreadyRevealed"),
              detail: t("submissiontoast.alreadyRevealedText"),
              life: 2500,
            });
            setUsedHints((prev) => new Set([...prev, i]));
            toggleHint(i);
          }
        } catch {
          toastRef.current?.show({
            severity: "error",
            summary: "Failed",
            detail: "Could not record hint usage.",
            life: 3000,
          });
        }
      },
    });
  }

  // team points polling
  useEffect(() => {
    async function loadTeamPoints() {
      try {
        const res = await fetch(
          apiUrl(`/public/games/${encodeURIComponent(gameCode)}/scoreboard`),
          { headers: { Accept: "application/json" } }
        );
        const data = await res.json().catch(() => ({}));
        const rows = Array.isArray(data) ? data : data.scoreboard ?? [];
        const meRow = rows.find(
          (r: ScoreboardDto) =>
            String(r.name ?? r.team ?? "").toLowerCase() ===
            teamName.toLowerCase()
        );
        setTeamPoints(Number(meRow?.points ?? meRow?.score ?? 0));
      } catch {
        /* A failed scoreboard read just leaves the last total on screen. */
      }
    }
    if (hasSession) loadTeamPoints();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameCode, teamName, scoreRevision, hasSession]);

  // load challenges
  useEffect(() => {
    if (!hasSession) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      setChallenges([]);

      const langForApi = i18n.language || "en"; //current UI language

      try {
        const res = await fetch(
          apiUrl(`/public/games/${encodeURIComponent(gameCode)}/challenges`, {
            teamName,
            lang: langForApi, //send lang to backend
          }),
          { headers: { Accept: "application/json" } }
        );
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        const rows: ChallengeDto[] = Array.isArray(data)
          ? data
          : data.challenges || [];
        const normalized: Challenge[] = rows.map((r) => {
          const c = r.content ?? {};
          return {
            id: r.challenge_id ?? r.id ?? r.key ?? "",
            name: c.title ?? r.key ?? "Untitled",
            description: c.description ?? "",
            category: r.category ?? c.category ?? "Uncategorized",
            difficulty: (r.difficulty ?? c.difficulty ?? "easy")?.toString(),
            duration: c.duration ?? "",
            scenarios: c.scenarios ?? undefined,
            points: r.points ?? c.points ?? 100,
            flagFormat: c.flag_format ?? c.flagFormat ?? undefined,
            raw: r,
            content: r.content,
            attachments: r.attachments ?? [],
            bundleUrl: r.bundle_url ?? null,
            bundleSize: r.bundle_size ?? null,
          };
        });
        if (!cancelled) setChallenges(normalized);

        const st = await fetch(
          apiUrl(`/public/games/${encodeURIComponent(gameCode)}/status`, {
            teamName,
            playerId: player.id,
          }),
          { headers: { Accept: "application/json" } }
        );

        if (st.ok && !cancelled) {
          const sdata = await st.json();
          const teamSolved = new Set<string>(sdata.team_solved || []);
          const playerSolved = new Set<string>(sdata.player_solved || []);

          const merged = new Set([...teamSolved, ...playerSolved]);
          const map: Record<string, boolean> = {};
          for (const id of merged) map[id] = true;

          setSolvedMap(map);
        }
      } catch (e) {
        if (!cancelled)
          setErr(e instanceof Error ? e.message : "Failed to load challenges");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [gameCode, teamName, i18n.language, reloadKey, hasSession, apiUrl, player.id]);

  const filtered = challenges.filter((c) => {
    const content = c.content ?? c.raw?.content ?? {};
    const title = c.name ?? content.title ?? "";
    const description = c.description ?? content.description ?? "";
    const category = (
      c.category ??
      c.raw?.category ??
      content.category ??
      "Uncategorized"
    )
      .toString()
      .trim();
    const cdiff = normalizeDifficulty(
      (c.difficulty ?? content.difficulty ?? "").toString()
    );

    const targetDiff =
      typeof diff === "string" ? normalizeDifficulty(diff) : "";
    const byDiff = targetDiff ? cdiff === targetDiff : true;

    const targetCat = typeof cat === "string" ? cat.trim() : "";
    const byCat = targetCat ? category === targetCat : true;

    const hay = `${title} ${description} ${category}`.toLowerCase();
    const byQuery = query ? hay.includes(query.toLowerCase()) : true;

    return byDiff && byCat && byQuery;
  });

  // Gate: until the welcome challenge is solved, the rest stay locked.
  const welcomeChallenge = challenges.find(isWelcome);
  const welcomeSolved = welcomeChallenge
    ? !!solvedMap[String(welcomeChallenge.id)]
    : true;
  const solvedCount = challenges.filter((c) => solvedMap[String(c.id)]).length;
  const hasFilters = !!(query || diff || cat);

  function makeAbsolute(p: string) {
    if (!p) return p;
    if (/^https?:\/\//i.test(p)) return p;
    const API = import.meta.env.VITE_API_BASE ?? "/api";
    try {
      if (/^https?:\/\//i.test(API)) {
        const u = new URL(API);
        return `${u.origin}${p}`;
      }
    } catch {
      /* A malformed VITE_API_BASE falls back to the relative path. */
    }
    return p;
  }

  async function openModal(c: Challenge) {
    setSelected(c);
    setFlag("");
    setOpenedHints(new Set());
    setShow(true);

    try {
      const res = await fetch(
        apiUrl(
          `/public/games/${encodeURIComponent(
            gameCode
          )}/challenges/${encodeURIComponent(c.id)}/hints/status`,
          { teamName }
        )
      );

      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        if (Array.isArray(data.usedHints)) {
          setUsedHints(new Set(data.usedHints));
          setOpenedHints(new Set(data.usedHints));
        }
      }
    } catch (e) {
      console.warn("Failed to load hint status", e);
    }
  }

  async function openHtmlAsBlob(url: string) {
    try {
      const res = await fetch(url, {
        cache: "no-store",
        headers: { Accept: "text/html" },
      });
      if (!res.ok) return false;

      let html = await res.text();
      if (!/name=['"]referrer['"]/i.test(html)) {
        html = html.replace(
          /<head([^>]*)>/i,
          `<head$1><meta name="referrer" content="no-referrer">`
        );
      }

      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      const objUrl = URL.createObjectURL(blob);
      window.open(objUrl, "_blank", "noopener,noreferrer");

      setTimeout(() => URL.revokeObjectURL(objUrl), 60_000);
      return true;
    } catch {
      return false;
    }
  }

  async function openAttachment(att: Attachment | undefined | null) {
    if (!att) return;
    const file = att.filename || "";
    const url = makeAbsolute(`/bundles/${encodeURIComponent(file)}`);
    const ext = file.split(".").pop()?.toLowerCase();

    if (ext === "html" || ext === "htm") {
      const ok = await openHtmlAsBlob(url);
      if (!ok) {
        toastRef.current?.show({
          severity: "warn",
          summary: "File not found",
          detail: "Attachment could not be opened.",
          life: 3000,
        });
      }
      return;
    }

    window.open(url, "_blank", "noopener,noreferrer");
  }

  async function handleSubmitFlag() {
    const welcome = isWelcome(selected);
    if (status !== "active" && !welcome) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Closed",
        detail: "Submissions are closed.",
        life: 2500,
      });
      return;
    }

    if (!selected) return;
    if (!flag.trim()) {
      toastRef.current?.show({
        severity: "warn",
        summary: t("submissiontoast.missingflag"),
        detail: t("submissiontoast.plsFlag"),
        life: 2500,
      });
      return;
    }

    try {
      setSubmitting(true);
      const body = {
        teamName, // latest state
        challengeId: selected.id,
        answer: { flag: flag.trim() },
        playerId: player.id,
      };

      const res = await fetch(
        apiUrl(`/public/games/${encodeURIComponent(gameCode)}/submit`),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(body),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const msg =
          data?.error === "already_submitted"
            ? t("submissiontoast.challengeCompleted")
            : data?.error === "incorrect_flag"
            ? t("submissiontoast.incorrectFlag")
            : data?.message || `Submit failed (${res.status})`;

        toastRef.current?.show({
          severity: "error",
          summary:
            data?.error === "incorrect_flag"
              ? t("submissiontoast.wrongFlag")
              : "Try again",
          detail: msg,
          life: 3000,
        });
        return;
      }

      const ok = !!data?.is_correct;
      const pts = data?.points_awarded ?? 0;
      const bonus = data?.bonus_awarded ?? 0;
      const penalty = data?.hint_penalty ?? 0;
      const net = data?.net_gain ?? Math.max(pts + bonus - penalty, 0);
      const bonusMsg = data?.bonus_message ?? "";

      const detailPieces: string[] = [];
      detailPieces.push(t("submissiontoast.pointsBase", { points: pts }));
      if (bonus > 0) {
        detailPieces.push(t("submissiontoast.pointsBonus", { bonus }));
      }
      if (penalty > 0) {
        detailPieces.push(t("submissiontoast.pointsPenalty", { penalty }));
      }
      detailPieces.push(t("submissiontoast.pointsTotal", { total: net }));

      const detailText = detailPieces.join(" ");

      if (ok && welcome) {
        toastRefRight.current?.show({
          severity: "info",
          summary: t("sidetoast.welcomeChallenge"),
          detail: t("sidetoast.welcomeChallengeText"),
          life: 4000,
        });
      } else {
        toastRef.current?.show({
          severity: ok ? "success" : "error",
          summary: ok
            ? t("submissiontoast.correctFlag")
            : t("submissiontoast.incorrectFlag"),
          detail: (
            <div
              style={{ whiteSpace: "pre-wrap", lineHeight: 1.4 }}
              dir={isRtl ? "rtl" : "ltr"}
              dangerouslySetInnerHTML={{ __html: detailText }}
            />
          ),
          life: 3000,
        });

        if (ok && bonusMsg) {
          toastRefRight.current?.show({
            severity: "info",
            summary: t("sidetoast.bonus"),
            detail: t(bonusMsg),
            life: 4000,
          });
        }
      }

      if (ok) {
        setSolvedMap((prev) => ({
          ...prev,
          [String(selected.id)]: true,
        }));
        setFlag("");
        setShow(false);
      }
    } catch (e) {
      toastRef.current?.show({
        severity: "error",
        summary: "Network error",
        detail: e instanceof Error ? e.message : "Failed to submit flag",
        life: 3000,
      });
    } finally {
      setSubmitting(false);
    }
  }

  const selectedIsWelcome = isWelcome(selected);
  const submitDisabled =
    submitting || (status !== "active" && !selectedIsWelcome);

  const hints: string[] = (() => {
    const c = selected;
    if (!c) return [];
    const fromArray = c.content?.hints;
    if (Array.isArray(fromArray)) return fromArray.slice(0, 3);
    const single = c.hint ?? c.content?.hint;
    return single ? [single] : [];
  })();

  function clearFilters() {
    setQuery("");
    setDiff(null);
    setCat(null);
  }

  /* Safe here: all hooks above have already run this render. */
  if (!hasSession) return <Navigate to="/" replace />;

  const headerActions = (
    <Button
      label={t("play.viewScoreboard")}
      icon="pi pi-chart-bar"
      className="p-button-sm"
      onClick={() =>
        window.open(
          `/scoreboard/${encodeURIComponent(gameCode)}`,
          "_blank",
          "noopener"
        )
      }
    />
  );

  return (
    <AppShell actions={headerActions} tagline={gameCode}>
      <AppToast ref={toastRef} position="bottom-center" />
      <AppToast ref={toastRefRight} position="top-right" />
      <ConfirmDialog />

      {/* ---------- Status banner ---------- */}
      <section className="hero rise">
        <div className="hero__top">
          <StatusPill status={status} />
          <GameCodeChip code={gameCode} />
          <span className="timer">
            <span className="timer__label">{t("ui.timeRemaining")}</span>
            {label()}
          </span>
        </div>

        <div className="hero__grid">
          <div>
            <h1 className="hero__title">
              {t("common.welcomePlayer")}, {player.name}{" "}
              <span>· {teamName}</span>
            </h1>
            <p className="hero__sub">{t("common.chooseChallenge")}</p>
          </div>

          <div className="stat">
            <span className="stat__label">{t("common.teamPoints")}</span>
            <div className="stat__row">
              <span className="stat__value">
                {isFinite(teamPoints) ? teamPoints.toLocaleString() : "0"}
              </span>
              <i className="pi pi-bolt stat__icon" aria-hidden="true" />
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Challenges ---------- */}
      <div className="section-head">
        <h2 className="section-head__title">{t("play.challengesTitle")}</h2>
        {!loading && challenges.length > 0 && (
          <span className="section-head__note num">
            {t("ui.solvedCount", {
              solved: solvedCount,
              total: challenges.length,
            })}
          </span>
        )}
      </div>

      <div className="toolbar">
        <span className="p-inputgroup">
          <span className="p-inputgroup-addon">
            <i className="pi pi-search" aria-hidden="true" />
          </span>
          <InputText
            className="w-full"
            placeholder={t("common.search")}
            value={query}
            aria-label={t("common.search")}
            onChange={(e) => setQuery(e.target.value)}
          />
        </span>

        <Dropdown
          value={diff}
          onChange={(e) => setDiff(e.value ?? null)}
          options={[
            { label: t("difficulty.alldifficulties"), value: null },
            { label: t("difficulty.easy"), value: "easy" },
            { label: t("difficulty.medium"), value: "medium" },
            { label: t("difficulty.hard"), value: "hard" },
          ]}
          placeholder={t("difficulty.difficulty")}
          aria-label={t("difficulty.difficulty")}
          className="w-full"
          showClear
        />

        <Dropdown
          value={cat}
          onChange={(e) => setCat(e.value ?? null)}
          options={[
            { label: t("play.allcategories"), value: null },
            ...categories.map((c) => ({ label: c, value: c })),
          ]}
          placeholder={t("play.category")}
          aria-label={t("play.category")}
          className="w-full"
          showClear
        />
      </div>

      <div style={{ marginTop: "var(--space-4)" }}>
        {loading && (
          <div className="card-grid">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="challenge">
                <Skeleton width="55%" height="1.3rem" />
                <Skeleton width="100%" height="3.2rem" className="mt-3" />
                <div
                  className="challenge__foot"
                  style={{ borderTop: 0, marginTop: "var(--space-4)" }}
                >
                  <Skeleton width="5rem" height="1rem" />
                  <Skeleton width="6rem" height="2.2rem" borderRadius="9px" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && err && (
          <EmptyState
            icon="pi pi-exclamation-triangle"
            title={t("ui.loadFailed")}
            text={err}
            action={
              <Button
                label={t("ui.retry")}
                icon="pi pi-refresh"
                className="p-button-sm"
                onClick={() => setReloadKey((k) => k + 1)}
              />
            }
          />
        )}

        {!loading && !err && filtered.length === 0 && (
          <EmptyState
            icon={hasFilters ? "pi pi-filter-slash" : "pi pi-inbox"}
            title={hasFilters ? t("ui.noResults") : t("play.nochallenges")}
            text={hasFilters ? t("ui.noResultsText") : undefined}
            action={
              hasFilters ? (
                <Button
                  label={t("ui.clearFilters")}
                  icon="pi pi-times"
                  className="p-button-sm p-button-outlined"
                  onClick={clearFilters}
                />
              ) : undefined
            }
          />
        )}

        {!loading && !err && filtered.length > 0 && (
          <div className="card-grid stagger">
            {filtered.map((c) => {
              const solved = !!solvedMap[String(c.id)];
              const welcome = isWelcome(c);
              const locked = !!welcomeChallenge && !welcome && !welcomeSolved;
              const featured = welcome && !solved;

              return (
                <article
                  key={c.id}
                  onMouseMove={onCardMove}
                  className={[
                    "challenge",
                    solved ? "challenge--solved" : "",
                    locked ? "challenge--locked" : "",
                    featured ? "challenge--featured" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <div className="challenge__head">
                    <h3 className="challenge__title">{c.name}</h3>
                    {solved ? (
                      <span className="solved-badge">
                        <i className="pi pi-check-circle" aria-hidden="true" />
                        {t("ui.solved")}
                      </span>
                    ) : (
                      typeof c.points === "number" && (
                        <span className="points">
                          <span className="points__value">{c.points}</span>
                          {t("play.points")}
                        </span>
                      )
                    )}
                  </div>

                  {c.description && (
                    <p className="challenge__desc">{c.description}</p>
                  )}

                  <div className="challenge__foot">
                    <div className="challenge__meta">
                      <DifficultyMeter value={c.difficulty} />
                      {c.category && <span>· {c.category}</span>}
                      {c.duration && <span>· {c.duration}</span>}
                    </div>

                    <Button
                      label={
                        solved
                          ? t("actions.submitted")
                          : locked
                          ? t("actions.locked")
                          : t("actions.start")
                      }
                      icon={
                        solved
                          ? "pi pi-check"
                          : locked
                          ? "pi pi-lock"
                          : "pi pi-play"
                      }
                      className="p-button-sm"
                      outlined={solved}
                      tooltip={locked ? t("ui.lockedHint") : undefined}
                      onClick={() => !solved && !locked && openModal(c)}
                      disabled={solved || locked}
                    />
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      {/* ---------- Challenge modal ---------- */}
      <Dialog
        header={selected?.name || selected?.content?.title || ""}
        visible={show}
        style={{ width: "44rem", maxWidth: "95vw" }}
        modal
        dismissableMask
        onHide={() => setShow(false)}
      >
        {selected && (
          <div>
            <div className="meta-row">
              <DifficultyMeter
                value={
                  selected.difficulty ||
                  selected.content?.difficulty
                }
              />
              {selected.category && (
                <span className="text-muted">· {selected.category}</span>
              )}
              {selected.content?.duration && (
                <span className="text-muted">
                  · {selected.content.duration}
                </span>
              )}
              {typeof selected.points === "number" && (
                <span className="points" style={{ marginInlineStart: "auto" }}>
                  <span className="points__value">
                    {selected.points}
                  </span>
                  {t("play.points")}
                </span>
              )}
            </div>

            <p className="prose">
              {selected.description ??
                selected.content?.description}
            </p>

            {!selectedIsWelcome && (
              <>
                <Divider />

                {(() => {
                  const atts: AttachmentDto[] =
                    selected?.raw?.attachments ??
                    selected?.attachments ??
                    [];
                  if (!Array.isArray(atts) || atts.length === 0) return null;

                  return (
                    <div style={{ marginBottom: "var(--space-5)" }}>
                      <p className="label-caps" style={{ marginBottom: 8 }}>
                        {t("ui.attachments")}
                      </p>
                      <div className="attachments">
                        {atts.map((att: AttachmentDto, i: number) => (
                          <Button
                            key={att.filename || i}
                            label={attachmentLabel(i, atts.length)}
                            icon="pi pi-external-link"
                            className="p-button-sm p-button-outlined"
                            onClick={() => openAttachment(att)}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })()}

                <div className="hints">
                  <span className="label-caps">{t("submitmodal.hints")}</span>
                  <div className="hints__dots">
                    {[0, 1, 2].map((i) => (
                      <button
                        key={i}
                        type="button"
                        className={`hint-dot ${
                          openedHints.has(i) ? "hint-dot--used" : ""
                        }`}
                        onClick={() => requestHint(i, hints)}
                        disabled={!hints[i]}
                        aria-expanded={openedHints.has(i)}
                        aria-label={`${t("submitmodal.revealHint")} ${i + 1}`}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </div>
                </div>

                {hints.some((h, i) => openedHints.has(i) && h) && (
                  <ul className="hint-list">
                    {hints.map(
                      (h, i) =>
                        openedHints.has(i) &&
                        h && <li key={i}>{h}</li>
                    )}
                  </ul>
                )}
              </>
            )}

            <Divider />

            <div className="flag-row">
              <span className="p-inputgroup">
                <span className="p-inputgroup-addon">
                  <i className="pi pi-flag" aria-hidden="true" />
                </span>
                <InputText
                  className="w-full force-ltr mono"
                  placeholder={t("submitmodal.flag")}
                  value={flag}
                  onChange={(e) => setFlag(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !submitDisabled && !submitting) {
                      e.preventDefault();
                      handleSubmitFlag();
                    }
                  }}
                />
              </span>
              <Button
                label={t("submitmodal.submit")}
                icon="pi pi-send"
                onClick={handleSubmitFlag}
                loading={submitting}
                disabled={submitDisabled}
              />
            </div>
          </div>
        )}
      </Dialog>
    </AppShell>
  );
}

/* helpers */
function safeParse<T>(raw: string | null): T | null {
  try {
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}
